import { motion } from "framer-motion";
import { Phone, Wrench, Rocket, Cpu, TrendingUp, ChevronRight } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface StepDetail {
  number: string;
  icon: typeof Phone;
  title: string;
  description: string;
  popoutTitle: string;
  popoutIntro: string;
  popoutItems: string[];
  popoutFooter: string;
  optional?: string;
}

const steps: StepDetail[] = [
  {
    number: "01",
    icon: Phone,
    title: "Free Strategy Call",
    description: "Map out goals, opportunities, and what your business needs first.",
    popoutTitle: "Step 01 — Free Strategy Call",
    popoutIntro: "A short, easy conversation where we look at:",
    popoutItems: [
      "Your current digital marketing setup",
      "The type of jobs you want more of",
      "What's working and what's not",
      "Where the quickest wins are for your trade",
      "Which part of our system you should start with"
    ],
    popoutFooter: "There's no pressure to buy anything and no push into a full package.\nThe only outcome is clarity: \"Here's what will make the biggest impact for your business right now.\""
  },
  {
    number: "02",
    icon: Wrench,
    title: "Build Your Foundation",
    description: "Professional website, Google Business Profile & review system — if that's what you need first.",
    optional: "Optional First Step",
    popoutTitle: "Step 02 — Build Your Foundation",
    popoutIntro: "You only start here if your digital \"first impression\" is holding you back.\nThis layer focuses on the things customers judge before they call:",
    popoutItems: [
      "A clean, professional, mobile-first website",
      "A fully optimised Google Business Profile",
      "Automated 5-star Google review collection system",
      "Suburb-by-suburb local SEO visibility",
      "Clean, consistent, professional branding"
    ],
    popoutFooter: "This step is about making you look like the top choice instantly — not about adding work.\nIf your foundation is already solid, we skip this step and move straight to Growth."
  },
  {
    number: "03",
    icon: Rocket,
    title: "Activate Growth",
    description: "Google Ads, Facebook Ads & targeted campaigns — only when you're ready for more leads.",
    optional: "Optional, if you're ready",
    popoutTitle: "Step 03 — Activate Growth",
    popoutIntro: "When you're ready for more work, this layer turns steady leads on — without random spikes.\nThis may include:",
    popoutItems: [
      "Targeted Google Ads for trade businesses",
      "Facebook Ads for construction companies",
      "High-converting booking/landing pages",
      "Suburb + job-type precision targeting",
      "Lead filtering so tyre-kickers don't come through"
    ],
    popoutFooter: "You decide when this step goes live.\nSome businesses start here immediately. Others activate it once their foundation is cleaned up."
  },
  {
    number: "04",
    icon: Cpu,
    title: "Automate & Optimise",
    description: "Instant replies, missed-call text-back & smart follow-up — never lose a job again.",
    optional: "Optional Upgrade",
    popoutTitle: "Step 04 — Automate & Optimise",
    popoutIntro: "This layer fixes the biggest reason trade businesses lose work: slow replies.\nIf it suits your workflow, automation can handle:",
    popoutItems: [
      "Instant SMS/AI replies to enquiries",
      "Missed-call text-back system",
      "Smart lead qualification sequences",
      "Follow-up reminders",
      "Nurture sequences that convert more enquiries"
    ],
    popoutFooter: "You only add this step if you want your business to run smoother — not because it's required.\n\nThink of it as hiring a digital assistant that works 24/7 without costing you wages."
  },
  {
    number: "05",
    icon: TrendingUp,
    title: "Scale Smart",
    description: "Grow into higher-value jobs and better suburbs — when the time is right.",
    optional: "Optional Long-Term Step",
    popoutTitle: "Step 05 — Scale Smart",
    popoutIntro: "Once the earlier steps are in place, this layer helps your business:",
    popoutItems: [
      "Expand into new, better-paying suburbs",
      "Improve job quality and project value",
      "Strengthen your brand recognition",
      "Understand which suburbs and services perform best",
      "Plan long-term, controlled growth",
      "Improve profitability with better project mix"
    ],
    popoutFooter: "This is not something you do early on.\nIt's an optional step for trade businesses who want to grow strategically and sustainably."
  }
];

const stepVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  })
};

const lineVariants = {
  hidden: {
    scaleY: 0,
    originY: 0
  },
  visible: {
    scaleY: 1,
    transition: {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
    }
  }
};

export function ProcessSection() {
  const [selectedStep, setSelectedStep] = useState<StepDetail | null>(null);

  return (
    <section id="process-section" className="section-padding bg-muted relative overflow-hidden" aria-labelledby="process-heading">
      {/* Background accent */}
      <div className="absolute inset-0 grid-pattern opacity-40" />
      <div className="absolute top-0 left-0 w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] bg-primary/[0.03] rounded-full blur-[80px] sm:blur-[100px] -translate-x-1/2 -translate-y-1/2" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center mb-6 sm:mb-8"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>How We Work</span>
          </div>
          <h2 id="process-heading" className="heading-section text-foreground">
            Your Roadmap to Reliable Growth
          </h2>
          <p className="text-muted-foreground text-base mt-3 max-w-xl mx-auto">
            Here's how we work together — a clear, step-by-step process to get your trade or construction business more leads, reviews and jobs.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            {/* Animated Vertical Line */}
            <motion.div
              variants={lineVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="absolute left-5 sm:left-6 top-8 bottom-8 w-[2px] bg-gradient-to-b from-primary via-primary/60 to-primary/20 hidden md:block rounded-full"
              aria-hidden="true"
            />

            <div className="space-y-3">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  custom={index}
                  variants={stepVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="relative flex items-start gap-3 sm:gap-4 group"
                >
                  {/* Icon Circle */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1 + 0.1,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                    className="relative z-10 w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 shadow-button"
                    aria-hidden="true"
                  >
                    <step.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>

                  {/* Content Card */}
                  <button
                    onClick={() => setSelectedStep(step)}
                    className="flex-1 bg-card rounded-lg p-4 sm:p-5 border border-border/50 shadow-card text-left transition-all duration-300 hover:border-primary/30 hover:shadow-lg hover:-translate-y-0.5 group/card cursor-pointer"
                    aria-label={`Learn more about ${step.title}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[0.6875rem] sm:text-xs font-bold text-primary/70 uppercase tracking-wider">
                          Step {step.number}
                        </span>
                        {step.optional && (
                          <span className="text-[0.5625rem] sm:text-[0.625rem] font-medium text-muted-foreground/60 uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded">
                            {step.optional}
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover/card:text-primary transition-colors" aria-hidden="true" />
                    </div>
                    <h3 className="text-[0.9375rem] sm:text-base font-bold text-foreground mb-1">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-[1.65] text-[0.9375rem] sm:text-base">
                      {step.description}
                    </p>
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step Detail Dialog */}
      <Dialog open={!!selectedStep} onOpenChange={(open) => !open && setSelectedStep(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedStep && (
            <>
              <DialogHeader className="pb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shadow-button">
                    <selectedStep.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  {selectedStep.optional && (
                    <span className="text-[0.625rem] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wide bg-muted px-2 py-1 rounded">
                      {selectedStep.optional}
                    </span>
                  )}
                </div>
                <DialogTitle className="text-lg sm:text-xl font-bold text-foreground">
                  {selectedStep.popoutTitle}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Intro text */}
                <p className="text-muted-foreground text-[0.9375rem] sm:text-base leading-relaxed whitespace-pre-line">
                  {selectedStep.popoutIntro}
                </p>

                {/* Bullet points */}
                <ul className="space-y-2.5 pl-1">
                  {selectedStep.popoutItems.map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05, duration: 0.3 }}
                      className="flex items-start gap-2.5 text-[0.9375rem] sm:text-base text-foreground"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" aria-hidden="true" />
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* Footer text */}
                <div className="pt-2 border-t border-border/50">
                  <p className="text-muted-foreground text-[0.9375rem] sm:text-base leading-relaxed whitespace-pre-line">
                    {selectedStep.popoutFooter}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
